<script setup lang="ts">
import BlogBreadcrumb from '@/components/blog/BlogBreadcrumb.vue'
import BlogCard from '@/components/blog/BlogCard.vue'
import BlogFAQ from '@/components/blog/BlogFaq.vue'
import type { Blog } from '@/data/blogs'
import {
  formatDateToISO,
  getBlogBySlug,
  getBlogFolderByCategory,
} from '@/data/blogs'
import { blogs } from '@/data/blogs'
import { getLatestBlogs } from '@/data/blogs'
import { easeOut, motion } from 'motion-v'

const route = useRoute()

// Get the slug from the URL (e.g., "why-restaurant-marketing-feels-complicated")
const slugFromUrl = route.path.split('/').pop()

const lastThreeBlogs = getLatestBlogs(
  blogs.filter(blog => blog.slug !== slugFromUrl),
  3,
)

const blog = getBlogBySlug(slugFromUrl?.toString() || '')

const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const paragraphVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.4, ease: easeOut },
    }
  },
}

const blogVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  'headline': blog?.seo?.title,
  'image': [blog?.image],
  'datePublished': formatDateToISO(blog?.seo?.datePublished || ''),
  // 'dateModified': blog?.seo?.dateModified,
  // 'author': blog?.seo?.author?.map(a => ({
  //   '@type': a['@type'],
  //   'name': a.name,
  //   'url': a.url,
  // })) || [],
}

const faqSchema = computed(() => {
  if (!blog?.faq?.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': blog.faq.map(item => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer,
      },
    })),
  }
})

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/`,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': 'Blog',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog`,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': blog?.category,
      'item': `${
        import.meta.env.VITE_MONOLITH_URL
      }/blog?category=${encodeURIComponent(blog?.category || '')}`,
    },
    {
      '@type': 'ListItem',
      'position': 4,
      'name': blog?.title,
      'item': `${
        import.meta.env.VITE_MONOLITH_URL
      }/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
    },
  ],
}

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${
        import.meta.env.VITE_MONOLITH_URL
      }/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(blogSchema), // BlogPosting
    },
    blog?.faq?.length
      ? {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(faqSchema.value), // FAQPage
        }
      : null,
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbSchema),
    },
  ].filter(Boolean),
})

useSeoMeta({
  title: blog?.seo?.title,
  description: blog?.seo?.description,
  ogTitle: blog?.seo?.title,
  ogDescription: blog?.seo?.description,
  ogImage: blog?.seo?.ogImage,
})
</script>
<template>
  <div class="isolate">
    <LayoutNavbar />

    <div
      class="mb-0 bg-gradient-to-b from-[#f8f8f0] from-50% to-[#f8f8f0] pt-4 md:shadow-2xl md:shadow-black/10"
    >
      <section
        class="mx-auto mb-8 max-w-screen-xl px-4 md:px-8 lg:max-w-screen-xl xl:px-8"
      >
        <BlogBreadcrumb :blog="blog as Blog" />
        <motion.h1
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-3xl font-medium text-gray-700 lg:text-4xl"
        >
          {{ blog?.title }}
        </motion.h1>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-gray-500"
        >
          If Diners Can’t Find You Online, You Don’t Exist
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-gray-500"
        >
          <router-link
            :to="{ path: '/blog', query: { category: blog?.category } }"
            class="text-[#FF7118] underline"
          >
            {{ blog?.category }}
          </router-link>
          • {{ blog?.date }}
        </motion.p>
        <motion.img
          :initial="{ opacity: 0, y: 50 }"
          :while-in-view="{
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: easeOut },
          }"
          :src="blog?.image"
          :alt="blog?.alt"
          class="mt-6 w-full rounded-[2.5rem] object-cover drop-shadow-lg"
        />
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          Restaurant Marketing in 2026: 30-Second Takeaways
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-gray-500"
        >
          Restaurant marketing is using digital channels—like SEO, your website,
          Google, reviews, email, and text—to help diners find you, choose you,
          and come back.
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Restaurants get more customers in 2026 by improving local search
              visibility, especially through their Google Business Profile and
              website SEO.</span>
          </li>
          <li>
            <span>
              Your Google Business Profile is the most important factor for
              restaurant discovery because it determines how often you appear in
              Google Maps and “near me” searches.
            </span>
          </li>
          <li>
            <span>
              Online reviews directly impact restaurant traffic, influencing
              both rankings and diner trust.
            </span>
          </li>
          <li>
            <span>
              High-quality photos increase conversion rates by helping diners
              quickly evaluate your experience.
            </span>
          </li>
          <li>
            <span>
              Restaurants stand out by clearly communicating what makes them
              different.
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Restaurant marketing in 2026 is the process of helping diners find
          your restaurant online, choose it over competitors, and return through
          digital channels like Google, your website, and online reviews.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Today, most dining decisions start on a mobile phone. Before a guest
          walks through your doors, they’ve already searched “restaurants near
          me,” compared options, read reviews, and looked at photos.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If your restaurant doesn’t appear—or doesn’t stand out—you’re not just
          missing clicks. You’re losing customers to competitors who are easier
          to find and trust.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          In 2026,
          <RouterLink :to="{ name: '/how-it-works' }">
            <span class="text-[#FF7118] underline">restaurant marketing</span>
          </RouterLink>
          isn’t just advertising. It’s your visibility, reputation, and ability
          to consistently fill tables.
        </motion.p>
        <div class="mt-3 rounded-3xl border border-neutral-200 bg-white px-6 py-4">
          <motion.p
            :variants="paragraphVariants"
            :custom="0"
            initial="initial"
            while-in-view="animate"
            class="text-xl font-semibold leading-relaxed text-gray-800"
          >
            If diners can’t find your restaurant when they search, nothing
            else—your food, service, or experience—has a chance to influence
            their decision.
          </motion.p>
        </div>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Do Diners Find Restaurants Online?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          <a
            href="https://www.fsrmagazine.com/food/menu-innovations/your-restaurant-customers-already-dropped-virtual-visit/"
            class="text-[#FF7118] underline"
            target="_blank"
          >86% of diners research a restaurant online before visiting
          </a>
          through Google by searching phrases like “restaurants near me” or
          “best brunch near me.” In fact, Google ranks restaurants based on
          three primary factors: relevance, distance, and prominence.
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Relevance comes from your website and business information
            </span>
          </li>
          <li>
            <span>Distance depends on the searcher’s location </span>
          </li>
          <li>
            <span>Prominence is influenced by reviews, ratings, and overall online
              presence
            </span>
          </li>
        </motion.ul>

        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="text-gray-700"
        >
          Google ranks restaurants based on:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>A fast, mobile-friendly website with clear menus and location
              details
            </span>
          </li>
          <li>
            <span>An optimized Google Business Profile with accurate, updated
              information
            </span>
          </li>
          <li>
            <span>High-quality photos of your food and space </span>
          </li>
          <li>
            <span>Consistent business details across the internet (name, address,
              phone)
            </span>
          </li>
          <li>
            <span>Strong review volume, recent ratings, and owner responses
            </span>
          </li>
        </motion.ul>
        <motion.h3
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-xl font-medium text-gray-700 lg:text-2xl"
        >
          Your Website
        </motion.h3>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Your website is one of the most important drivers of restaurant SEO.
          It should be fast, mobile-friendly, and built specifically for search
          visibility.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Your menu, hours, location, and key information should be easy to
          find. Each page should focus on a specific offering (catering, private
          dining, reservations), with clear calls to action. Menus should be
          published as searchable content, not PDFs.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If your website is slow, outdated, or difficult to navigate, Google
          will rank you lower, and most diners won’t scroll far enough to find
          you.
        </motion.p>
        <motion.h3
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-xl font-medium text-gray-700 lg:text-2xl"
        >
          Your Google Business Profile
        </motion.h3>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Your Google Business Profile is the most important ranking factor for
          local restaurant discovery because it determines your visibility in
          Google Search and Maps.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          It displays your hours, menu, photos, reviews, location, and
          directions—often before someone ever visits your website.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          To improve your visibility:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Respond to every review, positive or negative </span>
          </li>
          <li>
            <span>Select all relevant attributes and calls to action </span>
          </li>
          <li>
            <span>Keep menus, hours, and updates current </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If your restaurant doesn’t appear in top search results, you’re
          invisible at the exact moment someone is ready to choose where to eat.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Do You Get More Customers for Your Restaurant?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Restaurants get more customers by improving their visibility in search
          results, clearly communicating their value, and building trust through
          reviews and photos.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Restaurants attract more customers when they:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Rank in Google for “near me” searches </span>
          </li>
          <li>
            <span>
              Clearly describe their cuisine, experience, and differentiators
            </span>
          </li>
          <li>
            <span> Showcase high-quality photos </span>
          </li>
          <li>
            <span> Maintain strong, recent reviews </span>
          </li>
          <li>
            <span> Make it easy to reserve or order online </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          In 2026, diners have endless options—chains, delivery apps, ghost
          kitchens, and local competitors.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Your digital presence must answer one question instantly: Why this
          restaurant?
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Compare these two examples:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Italian fine dining </span>
          </li>
          <li>
            <span>
              Seasonal Italian cuisine featuring locally sourced ingredients and
              house-made pastas in an intimate setting
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          The second tells a story. Clear positioning turns searches into
          reservations.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If competitors communicate their value better online, they win the
          booking, even if your food is better.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Do Online Reviews and Photos Influence Restaurant Decisions?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Online reviews and photos are two of the most important factors
          influencing whether diners choose your restaurant.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Reviews build trust and improve rankings, while photos help diners
          quickly evaluate your experience.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          When comparing restaurants, diners typically choose the one with:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>More recent and higher-rated reviews </span>
          </li>
          <li>
            <span> A larger number of total reviews </span>
          </li>
          <li>
            <span> Clear, professional photos of food and ambiance </span>
          </li>
          <li>
            <span> Active responses from the business owner </span>
          </li>
        </motion.ul>
        <motion.h3
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-xl font-medium text-gray-700 lg:text-2xl"
        >
          Photos
        </motion.h3>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Photos help diners quickly decide if your restaurant matches their
          expectations.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          High-quality, up-to-date images increase clicks, engagement, and
          conversions. If your photos are outdated or missing, diners may assume
          the experience isn’t worth the visit.
        </motion.p>
        <motion.h3
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-xl font-medium text-gray-700 lg:text-2xl"
        >
          Google Reviews
        </motion.h3>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Google reviews influence both trust and visibility in search results
          because they act as ranking signals and social proof. In fact,
          <a
            href="https://sundayapp.com/google-reviews-why-they-matter-for-us-restaurants-e%E2%80%91reputation/#:~:text=4min-,Google%20Reviews:%20Why%20They%20Matter%20for%20US%20Restaurants%27%20E%E2%80%91,word%2Dof%2Dmouth%20reputation"
            target="_blank"
            class="text-[#FF7118] underline"
          >94% of U.S. diners base dining decisions on online reviews</a>, and 88% trust them as much as personal recommendations.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Google evaluates review quantity, recency, and ratings when ranking
          restaurants. Businesses with frequent, positive reviews and active
          owner responses are more likely to appear higher in search results—and
          get chosen.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Do You Turn First-Time Guests Into Repeat Customers?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Restaurants turn first-time guests into repeat customers by staying
          connected through email, text marketing, and ongoing engagement.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Repeat guests create predictable revenue. Without consistent
          follow-up, even satisfied diners forget to return.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Effective retention strategies include:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Email marketing campaigns </span>
          </li>
          <li>
            <span> SMS promotions and updates </span>
          </li>
          <li>
            <span> Social media engagement </span>
          </li>
          <li>
            <span> Special offers and loyalty incentives </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Staying top-of-mind fills slow nights, drives catering, and stabilizes
          revenue.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Restaurants that win in 2026 don’t just attract traffic. They operate
          <RouterLink :to="{ name: '/how-it-works' }">
            <span class="text-[#FF7118] underline">a connected marketing system</span>
          </RouterLink>
          that keeps customers coming back.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How to Improve Your Restaurant Marketing in 2026
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          To improve your restaurant marketing in 2026, focus on visibility,
          conversion, and retention.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Follow these steps:
        </motion.p>
        <motion.ol
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          style="list-style: auto"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside py-4 text-gray-500"
        >
          <li>
            <span>Optimize your Google Business Profile </span>
          </li>
          <li>
            <span> Improve your website speed and SEO </span>
          </li>
          <li>
            <span> Actively manage and respond to reviews </span>
          </li>
          <li>
            <span>Add high-quality photos regularly </span>
          </li>
          <li>
            <span>Build and use an email and text marketing list </span>
          </li>
        </motion.ol>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Every day your marketing isn’t optimized, competitors are capturing
          your potential customers.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Not because they’re better restaurants, but because they’re easier to
          find.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          What Is the Most Important Factor in Restaurant Marketing?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          The most important factor in restaurant marketing is visibility in
          local search results, especially on Google.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If diners can’t find your restaurant when they search, nothing
          else—your food, service, or experience—has a chance to influence their
          decision.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Your website, Google Business Profile, and reviews work together to
          determine whether you appear, and whether diners choose you.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If you’d like to simplify your marketing with minimal effort,
          SpotHopper builds SEO-optimized restaurant websites synced with your
          Google Business Profile, automated review management, and AI-powered
          email, text campaigns, and social media—all designed to help
          independent restaurants compete and fill more tables.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          <RouterLink
            class="text-[#FF7118] underline"
            :to="{ name: '/how-it-works' }"
          >
            Learn More
          </RouterLink>
          or
          <RouterLink
            class="text-[#FF7118] underline"
            :to="{ name: '/free-demo' }"
          >
            Book a Free Demo
          </RouterLink>
        </motion.p>
      </section>
      <div class="mb-8 w-full bg-white pb-8 pt-8 md:rounded-[5rem]">
        <BlogFAQ :items="blog?.faq" />
        <section class="mx-auto mb-8 px-2 lg:max-w-screen-xl 2xl:px-0">
          <div class="grid w-full grid-cols-1 gap-x-6 gap-y-12 py-6 md:grid-cols-3">
            <motion.div
              v-for="(item, index) in lastThreeBlogs"
              :key="item.slug"
              :custom="index"
              :variants="blogVariants"
              initial="initial"
              while-in-view="animate"
            >
              <BlogCard v-bind="item" />
            </motion.div>
          </div>
        </section>
        <section class="mx-auto px-2 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
          <div
            class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20"
          >
            <div
              class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70"
            >
              <motion.img
                :initial="{ opacity: 0, y: 50 }"
                :while-in-view="{
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.5, ease: easeOut },
                }"
                :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                class="aspect-video size-full object-cover object-center"
                role="presentation"
              />
            </div>

            <div class="isolate grid place-items-center gap-5">
              <motion.h3
                :variants="headlineVariants"
                initial="initial"
                while-in-view="animate"
                class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
              >
                Ready to take your restaurant marketing to the next level?
              </motion.h3>

              <UiButton
                class="group w-full max-w-xs lg:max-w-max"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  Book a Free Demo

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>
        </section>
      </div>

      <LayoutFooter />
    </div>
  </div>
</template>
